
<template>
  <aside class="sidebar-container">

    <!-- ========================================================
         HEADER
    ========================================================= -->

    <header class="sidebar-header">

      <div class="header-top">
        <div class="header-caption">
          <span class="header-eyebrow">
            HR3 MESSENGER
          </span>

          <span class="header-subtitle">
            Ваши разговоры
          </span>
        </div>

        <!-- CREATE CHAT -->

        <button
          type="button"
          class="create-chat-button"
          title="Новый диалог"
          aria-label="Создать диалог"
          @click="handleCreateChat"
        >
          <svg viewBox="0 0 24 24" fill="none">
            <path
              d="M12 5V19M5 12H19"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
        </button>
      </div>

      <!-- CATEGORY -->

      <div class="header-category-row">

        <div
          ref="categoryMenuRef"
          class="category-wrapper"
        >
          <button
            type="button"
            class="category-trigger"
            :aria-expanded="dropdownVisible"
            aria-haspopup="menu"
            @click="toggleDropdown"
          >
            <span class="category-title">
              {{ selectedCategory || "Сообщения" }}
            </span>

            <svg
              class="category-chevron"
              :class="{ rotated: dropdownVisible }"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M6 9L12 15L18 9"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>

          <Transition name="category-dropdown">
            <div
              v-if="dropdownVisible"
              class="category-dropdown"
              role="menu"
            >
              <button
                v-for="option in categories"
                :key="option"
                type="button"
                class="category-option"
                :class="{
                  active: selectedCategory === option
                }"
                role="menuitemradio"
                :aria-checked="selectedCategory === option"
                @click="selectCategory(option)"
              >
                <span>{{ option }}</span>

                <svg
                  v-if="selectedCategory === option"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M5 12L10 17L19 7"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
          </Transition>
        </div>

        <!-- UNREAD BADGE -->

        <span
          class="unread-badge"
          :class="{
            'has-unread': totalUnreadCount > 0
          }"
        >
          <span
            v-if="totalUnreadCount > 0"
            class="unread-dot"
          ></span>

          {{
            totalUnreadCount > 0
              ? `${totalUnreadCount} новых`
              : "Нет новых"
          }}
        </span>

      </div>
    </header>

    <!-- ========================================================
         SEARCH
    ========================================================= -->

    <div
      v-if="selectedCategory === 'Сообщения'"
      class="search-section"
    >
      <div class="search-field">

        <svg
          class="search-icon"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="10.8"
            cy="10.8"
            r="6.8"
            stroke="currentColor"
            stroke-width="1.8"
          />
          <path
            d="M16 16L21 21"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
          />
        </svg>

        <input
          v-model="searchQuery"
          type="search"
          placeholder="Поиск по диалогам"
          aria-label="Поиск по диалогам"
          autocomplete="off"
        />

        <button
          v-if="searchQuery"
          type="button"
          class="clear-search-button"
          title="Очистить поиск"
          aria-label="Очистить поиск"
          @click="clearSearch"
        >
          <svg viewBox="0 0 24 24" fill="none">
            <path
              d="M6 6L18 18M18 6L6 18"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
        </button>
      </div>
    </div>

    <!-- ========================================================
         LIST HEADER
    ========================================================= -->

    <div class="list-header">
      <span class="list-title">
        {{
          selectedCategory === "Сообщения"
            ? "Недавние диалоги"
            : "Входящие запросы"
        }}
      </span>

      <span class="list-counter">
        {{
          selectedCategory === "Сообщения"
            ? filteredDialogs.length
            : chatRequests.length
        }}
      </span>
    </div>

    <!-- ========================================================
         DIALOGS LIST
    ========================================================= -->

    <div class="dialogs-list">

      <!-- LOADING -->

      <div
        v-if="isLoading"
        class="skeleton-list"
      >
        <div
          v-for="i in 5"
          :key="i"
          class="dialog-skeleton"
        >
          <div class="avatar-skeleton shimmer"></div>

          <div class="skeleton-content">
            <div class="skeleton-header">
              <div class="skeleton-line name-line shimmer"></div>
              <div class="skeleton-line time-line shimmer"></div>
            </div>

            <div class="skeleton-line message-line shimmer"></div>

            <div class="skeleton-tags">
              <div class="skeleton-pill shimmer"></div>
              <div class="skeleton-pill small shimmer"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- ======================================================
           MESSAGES
      ======================================================= -->

      <template
        v-else-if="selectedCategory === 'Сообщения'"
      >
        <TransitionGroup
          name="dialog-delete"
          tag="div"
          class="dialogs-transition-list"
        >
          <DialogItem
            v-for="dialog in filteredDialogs"
            :key="dialog.id"
            :dialog="dialog"
            :selected="selectedDialogId === dialog.id"
            @select="handleSelectDialog"
            @delete="handleDeleteDialog"
          />
        </TransitionGroup>

        <!-- NO SEARCH RESULTS -->

        <div
          v-if="filteredDialogs.length === 0 && searchQuery.trim()"
          class="custom-empty-state"
        >
          <div class="empty-icon">
            <svg viewBox="0 0 24 24" fill="none">
              <circle
                cx="10.8"
                cy="10.8"
                r="6.8"
                stroke="currentColor"
                stroke-width="1.7"
              />
              <path
                d="M16 16L21 21"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
              />
            </svg>
          </div>

          <h3>Ничего не найдено</h3>
          <p>Попробуйте изменить поисковый запрос</p>

          <button
            type="button"
            class="reset-search-button"
            @click="clearSearch"
          >
            Сбросить поиск
          </button>
        </div>

        <!-- NO DIALOGS -->

        <EmptyDialogsPlaceholder
          v-else-if="filteredDialogs.length === 0"
          class="empty-state"
        />
      </template>

      <!-- ======================================================
           REQUESTS
      ======================================================= -->

      <template v-else-if="selectedCategory === 'Запросы'">

        <div
          v-if="chatRequests.length > 0"
          class="requests-list"
        >
          <RequestItem
            v-for="request in chatRequests"
            :key="request.id"
            :request="request"
            :selected="selectedRequestId === request.id"
            @select="handleSelectRequest"
            @accept="handleAcceptRequest"
            @decline="handleDeclineRequest"
          />
        </div>

        <EmptyRequestsDialogsPlaceholder
          v-else
          class="empty-state"
        />
      </template>

    </div>

    <div class="right-separator"></div>

  </aside>
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount
} from "vue";

import DialogItem from "./DialogItem.vue";
import RequestItem from "./RequestItem.vue";
import EmptyDialogsPlaceholder from "./EmptyDialogsPlaceholder.vue";
import EmptyRequestsDialogsPlaceholder from "./EmptyRequestsDialogsPlaceholder.vue";

// ============================================================
// PROPS
// ============================================================

const props = defineProps({
  dialogs: {
    type: Array,
    default: () => []
  },

  selectedDialogId: {
    type: [String, Number],
    default: null
  },

  selectedRequestId: {
    type: [String, Number],
    default: null
  },

  selectedCategory: {
    type: String,
    default: "Сообщения"
  },

  chatRequests: {
    type: Array,
    default: () => []
  },

  isLoading: {
    type: Boolean,
    default: false
  },

  totalUnreadCount: {
    type: Number,
    default: 0
  }
});

// ============================================================
// EMITS
// ============================================================

const emit = defineEmits([
  "selectDialog",
  "selectRequest",
  "accept",
  "decline",
  "addNewChat",
  "deleteDialog",
  "update:selectedCategory",
  "selectCategory"
]);

// ============================================================
// STATE
// ============================================================

const categories = [
  "Сообщения",
  "Запросы"
];

const searchQuery = ref("");
const dropdownVisible = ref(false);
const categoryMenuRef = ref(null);

// ============================================================
// FILTERED DIALOGS
// ============================================================

const filteredDialogs = computed(() => {
  const dialogs = props.dialogs || [];

  const query = searchQuery.value
    .trim()
    .toLocaleLowerCase("ru-RU");

  if (!query) {
    return dialogs;
  }

  return dialogs.filter((dialog) => {
    const name =
      dialog.interlocutor?.name || "";

    const specifics =
      dialog.interlocutor?.specifics || "";

    const message =
      dialog.lastMessageText || "";

    const searchableText = [
      name,
      specifics,
      message
    ]
      .join(" ")
      .toLocaleLowerCase("ru-RU");

    return searchableText.includes(query);
  });
});

// ============================================================
// CATEGORY
// ============================================================

function toggleDropdown() {
  dropdownVisible.value = !dropdownVisible.value;
}

function selectCategory(option) {
  if (props.selectedCategory !== option) {
    emit("update:selectedCategory", option);
    emit("selectCategory", option);
  }

  dropdownVisible.value = false;
  searchQuery.value = "";
}

// ============================================================
// SEARCH
// ============================================================

function clearSearch() {
  searchQuery.value = "";
}

// ============================================================
// DIALOG ACTIONS
// ============================================================

function handleSelectDialog(id) {
  emit("selectDialog", id);
}

function handleDeleteDialog(dialog) {
  emit("deleteDialog", dialog);
}

// ============================================================
// REQUEST ACTIONS
// ============================================================

function handleSelectRequest(id) {
  emit("selectRequest", id);
}

function handleAcceptRequest(id) {
  emit("accept", id);
}

function handleDeclineRequest(id) {
  emit("decline", id);
}

// ============================================================
// CREATE CHAT
// ============================================================

function handleCreateChat() {
  emit("addNewChat");
}

// ============================================================
// CLOSE DROPDOWN
// ============================================================

function handleOutsideClick(event) {
  if (!dropdownVisible.value) return;

  if (
    categoryMenuRef.value &&
    !categoryMenuRef.value.contains(event.target)
  ) {
    dropdownVisible.value = false;
  }
}

function handleKeydown(event) {
  if (event.key === "Escape") {
    dropdownVisible.value = false;
  }
}

// ============================================================
// LIFECYCLE
// ============================================================

onMounted(() => {
  document.addEventListener(
    "pointerdown",
    handleOutsideClick
  );

  document.addEventListener(
    "keydown",
    handleKeydown
  );
});

onBeforeUnmount(() => {
  document.removeEventListener(
    "pointerdown",
    handleOutsideClick
  );

  document.removeEventListener(
    "keydown",
    handleKeydown
  );
});
</script>

<style scoped>

/* ============================================================
   SIDEBAR — BASE
============================================================ */

.sidebar-container {
  --sidebar-bg: #ffffff;
  --sidebar-surface: #ffffff;
  --sidebar-text: #222b38;
  --sidebar-secondary: #8692a3;
  --sidebar-border: rgba(31, 55, 90, 0.07);
  --sidebar-accent: #2173e5;

  position: relative;

  display: flex;
  flex-direction: column;

  width: 400px;
  max-width: 100%;
  height: 100vh;
  min-height: 0;

  overflow: hidden;

  box-sizing: border-box;

  background: var(--sidebar-bg);
  border-right: 1px solid var(--sidebar-border);
}

/* ============================================================
   HEADER
============================================================ */

.sidebar-header {
  position: relative;
  z-index: 30;

  flex-shrink: 0;

  padding: 23px 18px 16px;

  background: var(--sidebar-surface);

  border-bottom: 1px solid var(--sidebar-border);
}

.header-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  gap: 16px;
}

.header-caption {
  display: flex;
  flex-direction: column;

  gap: 6px;
}

.header-eyebrow {
  font-family: "Eastman-Medium", sans-serif;
  font-size: 9px;
  font-weight: 500;

  letter-spacing: 1.4px;

  color: var(--sidebar-accent);
}

.header-subtitle {
  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;

  color: var(--sidebar-secondary);
}

/* ============================================================
   CREATE CHAT BUTTON
============================================================ */

.create-chat-button {
  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  width: 37px;
  height: 37px;

  padding: 0;

  border: 1px solid rgba(33, 115, 229, 0.09);
  border-radius: 12px;

  background: linear-gradient(
    135deg,
    #3188f6 0%,
    #2173e5 100%
  );

  color: #ffffff;

  cursor: pointer;

  box-shadow:
    0 5px 13px rgba(33, 115, 229, 0.17);

  transition:
    transform 0.18s ease,
    box-shadow 0.18s ease;
}

.create-chat-button svg {
  width: 19px;
  height: 19px;
}

.create-chat-button:hover {
  transform: translateY(-2px);

  box-shadow:
    0 8px 17px rgba(33, 115, 229, 0.25);
}

.create-chat-button:active {
  transform: scale(0.96);
}

.create-chat-button:focus-visible {
  outline: 2px solid var(--sidebar-accent);
  outline-offset: 3px;
}

/* ============================================================
   CATEGORY HEADER
============================================================ */

.header-category-row {
  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 12px;

  margin-top: 22px;
}

.category-wrapper {
  position: relative;

  min-width: 0;
}

.category-trigger {
  display: inline-flex;
  align-items: center;

  gap: 10px;

  padding: 0;

  border: none;
  background: transparent;

  color: var(--sidebar-text);

  cursor: pointer;
}

.category-title {
  font-family: "Eastman-Bold", sans-serif;
  font-size: 23px;
  font-weight: 700;
  line-height: 1.15;

  letter-spacing: -0.8px;
}

.category-chevron {
  width: 17px;
  height: 17px;

  margin-top: 3px;

  color: var(--sidebar-secondary);

  transition:
    transform 0.2s ease,
    color 0.2s ease;
}

.category-chevron.rotated {
  transform: rotate(180deg);
}

.category-trigger:hover .category-chevron {
  color: var(--sidebar-accent);
}

.category-trigger:focus-visible {
  outline: 2px solid var(--sidebar-accent);
  outline-offset: 5px;
  border-radius: 4px;
}

/* ============================================================
   UNREAD BADGE
============================================================ */

.unread-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 6px;

  flex-shrink: 0;

  padding: 7px 10px;

  border-radius: 9px;

  background: #f3f5f8;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 10px;

  color: #9aa3af;

  white-space: nowrap;
}

.unread-badge.has-unread {
  color: #2173e5;

  background: rgba(33, 115, 229, 0.08);
}

.unread-dot {
  width: 6px;
  height: 6px;

  flex-shrink: 0;

  border-radius: 50%;

  background: #2173e5;
}

/* ============================================================
   CATEGORY DROPDOWN
============================================================ */

.category-dropdown {
  position: absolute;

  top: calc(100% + 13px);
  left: 0;

  z-index: 100;

  width: 190px;

  padding: 6px;

  border: 1px solid var(--sidebar-border);
  border-radius: 15px;

  background: var(--sidebar-surface);

  box-shadow:
    0 14px 40px rgba(28, 42, 63, 0.14),
    0 2px 8px rgba(28, 42, 63, 0.035);

  box-sizing: border-box;
}

.category-option {
  display: flex;
  align-items: center;
  justify-content: space-between;

  width: 100%;

  padding: 12px 11px;

  border: none;
  border-radius: 10px;

  background: transparent;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 12px;

  color: var(--sidebar-text);

  text-align: left;

  cursor: pointer;

  transition:
    background 0.16s ease,
    color 0.16s ease;
}

.category-option svg {
  width: 16px;
  height: 16px;

  flex-shrink: 0;

  color: var(--sidebar-accent);
}

.category-option:hover {
  background: rgba(33, 115, 229, 0.055);
}

.category-option.active {
  color: var(--sidebar-accent);

  background: rgba(33, 115, 229, 0.08);
}

.category-option:focus-visible {
  outline: 2px solid var(--sidebar-accent);
  outline-offset: -2px;
}

/* ============================================================
   CATEGORY DROPDOWN ANIMATION
============================================================ */

.category-dropdown-enter-active,
.category-dropdown-leave-active {
  transition:
    opacity 0.16s ease,
    transform 0.16s ease;
}

.category-dropdown-enter-from,
.category-dropdown-leave-to {
  opacity: 0;

  transform: translateY(-6px) scale(0.97);
}

.category-dropdown-enter-to,
.category-dropdown-leave-from {
  opacity: 1;

  transform: translateY(0) scale(1);
}

/* ============================================================
   SEARCH SECTION
============================================================ */

.search-section {
  flex-shrink: 0;

  padding: 16px 15px 12px;

  background: var(--sidebar-bg);
}

.search-field {
  position: relative;

  display: flex;
  align-items: center;

  width: 100%;
  height: 44px;

  border: 1px solid rgba(37, 62, 96, 0.065);
  border-radius: 13px;

  background: var(--sidebar-surface);

  box-shadow:
    0 3px 12px rgba(30, 54, 90, 0.025);

  transition:
    border-color 0.18s ease,
    box-shadow 0.18s ease;
}

.search-field:focus-within {
  border-color: rgba(33, 115, 229, 0.36);

  box-shadow:
    0 0 0 3px rgba(33, 115, 229, 0.06);
}

.search-icon {
  width: 18px;
  height: 18px;

  flex-shrink: 0;

  margin-left: 14px;

  color: #9ca7b5;
}

.search-field input {
  flex: 1;

  width: 100%;
  min-width: 0;
  height: 100%;

  padding: 0 11px;

  border: none;
  outline: none;

  background: transparent;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 12px;

  color: var(--sidebar-text);
}

.search-field input::placeholder {
  color: #a5afbb;
}

.search-field input::-webkit-search-cancel-button {
  display: none;
}

/* ============================================================
   CLEAR SEARCH BUTTON
============================================================ */

.clear-search-button {
  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  width: 28px;
  height: 28px;

  margin-right: 8px;
  padding: 0;

  border: none;
  border-radius: 8px;

  background: #f1f3f7;

  color: #8a95a5;

  cursor: pointer;

  transition:
    color 0.18s ease,
    background 0.18s ease;
}

.clear-search-button svg {
  width: 14px;
  height: 14px;
}

.clear-search-button:hover {
  color: #2173e5;

  background: rgba(33, 115, 229, 0.08);
}

/* ============================================================
   LIST HEADER
============================================================ */

.list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;

  flex-shrink: 0;

  padding: 11px 19px 13px;
}

.list-title {
  font-family: "Eastman-Medium", sans-serif;
  font-size: 10px;

  color: var(--sidebar-secondary);
}

.list-counter {
  display: flex;
  align-items: center;
  justify-content: center;

  min-width: 21px;
  height: 21px;

  padding: 0 5px;

  border-radius: 7px;

  background: rgba(33, 115, 229, 0.065);

  font-family: "Eastman-Medium", sans-serif;
  font-size: 10px;

  color: #2173e5;

  box-sizing: border-box;
}

/* ============================================================
   DIALOGS LIST
============================================================ */

.dialogs-list {
  display: flex;
  flex-direction: column;

  flex: 1;

  min-height: 0;

  padding: 0 10px 24px;

  overflow-x: hidden;
  overflow-y: auto;

  scrollbar-width: thin;
  scrollbar-color: rgba(90, 115, 150, 0.2) transparent;

  overscroll-behavior: contain;
}

.dialogs-list::-webkit-scrollbar {
  width: 5px;
}

.dialogs-list::-webkit-scrollbar-track {
  background: transparent;
}

.dialogs-list::-webkit-scrollbar-thumb {
  background: rgba(90, 115, 150, 0.2);

  border-radius: 99px;
}

.dialogs-list::-webkit-scrollbar-thumb:hover {
  background: rgba(90, 115, 150, 0.35);
}

/* ============================================================
   DIALOGS AND REQUESTS
============================================================ */

.dialogs-transition-list,
.requests-list {
  display: flex;
  flex-direction: column;

  gap: 2px;

  width: 100%;
  min-width: 0;
}

/* ============================================================
   EMPTY STATE — EXISTING COMPONENTS
============================================================ */

.dialogs-list .empty-state {
  width: 100%;
  max-width: 330px;

  margin: auto;

  text-align: center;
}

/* ============================================================
   EMPTY SEARCH RESULT
============================================================ */

.custom-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  gap: 10px;

  min-height: 260px;

  padding: 30px 16px;

  margin: auto 0;

  text-align: center;
}

.empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 56px;
  height: 56px;

  margin-bottom: 5px;

  border-radius: 18px;

  background: rgba(33, 115, 229, 0.075);

  color: #2173e5;
}

.empty-icon svg {
  width: 25px;
  height: 25px;
}

.custom-empty-state h3 {
  margin: 0;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 14px;

  color: var(--sidebar-text);
}

.custom-empty-state p {
  margin: 0;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;

  color: var(--sidebar-secondary);
}

.reset-search-button {
  margin-top: 8px;

  padding: 9px 15px;

  border: 1px solid rgba(33, 115, 229, 0.12);
  border-radius: 9px;

  background: rgba(33, 115, 229, 0.065);

  font-family: "Eastman-Medium", sans-serif;
  font-size: 11px;

  color: #2173e5;

  cursor: pointer;

  transition:
    background 0.18s ease,
    transform 0.18s ease;
}

.reset-search-button:hover {
  background: rgba(33, 115, 229, 0.11);

  transform: translateY(-1px);
}

/* ============================================================
   SKELETON LIST
============================================================ */

.skeleton-list {
  display: flex;
  flex-direction: column;

  gap: 9px;
}

.dialog-skeleton {
  display: flex;
  align-items: center;

  gap: 13px;

  width: 100%;
  min-height: 100px;

  padding: 15px 13px;

  border: 1px solid var(--sidebar-border);
  border-radius: 17px;

  background: var(--sidebar-surface);

  box-sizing: border-box;
}

.avatar-skeleton {
  width: 54px;
  height: 54px;

  flex-shrink: 0;

  border-radius: 16px;
}

.skeleton-content {
  display: flex;
  flex-direction: column;

  gap: 12px;

  flex: 1;

  min-width: 0;
}

.skeleton-header {
  display: flex;
  justify-content: space-between;

  gap: 12px;
}

.skeleton-line {
  height: 10px;

  border-radius: 5px;
}

.name-line {
  width: 55%;
  height: 12px;
}

.time-line {
  width: 34px;
}

.message-line {
  width: 84%;
  height: 9px;
}

.skeleton-tags {
  display: flex;

  gap: 7px;
}

.skeleton-pill {
  width: 95px;
  height: 18px;

  border-radius: 7px;
}

.skeleton-pill.small {
  width: 55px;
}

/* ============================================================
   SKELETON SHIMMER
============================================================ */

.shimmer {
  background: linear-gradient(
    110deg,
    #edf0f5 8%,
    #f7f9fc 18%,
    #edf0f5 33%
  );

  background-size: 220% 100%;

  animation: skeleton-shimmer 1.6s linear infinite;
}

@keyframes skeleton-shimmer {
  to {
    background-position-x: -220%;
  }
}

/* ============================================================
   DIALOG DELETE ANIMATION
============================================================ */

.dialog-delete-enter-active,
.dialog-delete-leave-active,
.dialog-delete-move {
  transition:
    opacity 0.28s ease,
    transform 0.34s cubic-bezier(0.22, 1, 0.36, 1);
}

.dialog-delete-leave-active {
  position: absolute;

  left: 0;

  width: 100%;

  pointer-events: none;
}

.dialog-delete-leave-to {
  opacity: 0;

  transform: translateX(-35px) scale(0.96);
}

.dialog-delete-enter-from {
  opacity: 0;

  transform: translateY(-12px) scale(0.98);
}

.dialog-delete-enter-to,
.dialog-delete-leave-from {
  opacity: 1;

  transform: translateX(0) translateY(0) scale(1);
}

/* ============================================================
   RIGHT SEPARATOR
============================================================ */

.right-separator {
  position: absolute;

  top: 0;
  right: 0;
  bottom: 0;

  z-index: 1;

  width: 1px;

  background: var(--sidebar-border);

  pointer-events: none;
}

/* ============================================================
   DARK THEME — SCOPED ONLY
============================================================ */

html[data-theme="dark"] .sidebar-container {
  --sidebar-bg: #161616;
  --sidebar-surface: #161616;
  --sidebar-text: #f0f2f5;
  --sidebar-secondary: #818c9b;
  --sidebar-border: rgba(255, 255, 255, 0.055);
  --sidebar-accent: #83b6ff;
}

/* ============================================================
   DARK — CREATE BUTTON
============================================================ */

html[data-theme="dark"] .create-chat-button {
  background: linear-gradient(
    135deg,
    #327fdc,
    #295e9e
  );

  border-color: rgba(111, 168, 255, 0.12);

  box-shadow:
    0 5px 15px rgba(0, 0, 0, 0.2);
}

/* ============================================================
   DARK — UNREAD BADGE
============================================================ */

html[data-theme="dark"] .unread-badge {
  background: #30353e;

  color: #8994a2;
}

html[data-theme="dark"] .unread-badge.has-unread {
  background: rgba(72, 143, 255, 0.13);

  color: #83b6ff;
}

html[data-theme="dark"] .unread-dot {
  background: #83b6ff;
}

/* ============================================================
   DARK — CATEGORY
============================================================ */

html[data-theme="dark"] .category-option:hover {
  background: rgba(111, 168, 255, 0.09);
}

html[data-theme="dark"] .category-option.active {
  background: rgba(111, 168, 255, 0.12);

  color: #83b6ff;
}

html[data-theme="dark"] .category-dropdown {
  box-shadow:
    0 18px 45px rgba(0, 0, 0, 0.35);
}

/* ============================================================
   DARK — SEARCH
============================================================ */

html[data-theme="dark"] .search-field {
  border-color: rgba(255, 255, 255, 0.06);

  box-shadow:
    0 3px 12px rgba(0, 0, 0, 0.07);
}

html[data-theme="dark"] .search-field:focus-within {
  border-color: rgba(111, 168, 255, 0.35);

  box-shadow:
    0 0 0 3px rgba(111, 168, 255, 0.07);
}

html[data-theme="dark"] .search-icon {
  color: #788493;
}

html[data-theme="dark"] .search-field input::placeholder {
  color: #788493;
}

html[data-theme="dark"] .clear-search-button {
  background: #343a44;

  color: #a3adba;
}

html[data-theme="dark"] .clear-search-button:hover {
  background: rgba(111, 168, 255, 0.12);

  color: #83b6ff;
}

/* ============================================================
   DARK — LIST COUNTER
============================================================ */

html[data-theme="dark"] .list-counter {
  color: #83b6ff;

  background: rgba(111, 168, 255, 0.12);
}

/* ============================================================
   DARK — EMPTY STATE
============================================================ */

html[data-theme="dark"] .empty-icon {
  background: rgba(111, 168, 255, 0.1);

  color: #83b6ff;
}

html[data-theme="dark"] .reset-search-button {
  background: rgba(111, 168, 255, 0.1);

  border-color: rgba(111, 168, 255, 0.14);

  color: #83b6ff;
}

html[data-theme="dark"] .reset-search-button:hover {
  background: rgba(111, 168, 255, 0.16);
}

/* ============================================================
   DARK — SKELETON
============================================================ */

html[data-theme="dark"] .shimmer {
  background: linear-gradient(
    110deg,
    #30353e 8%,
    #3b414b 18%,
    #30353e 33%
  );

  background-size: 220% 100%;
}

html[data-theme="dark"] .dialog-skeleton {
  border-color: rgba(255, 255, 255, 0.045);
}

/* ============================================================
   DARK — SCROLLBAR
============================================================ */

html[data-theme="dark"] .dialogs-list {
  scrollbar-color: rgba(170, 185, 205, 0.18) transparent;
}

html[data-theme="dark"] .dialogs-list::-webkit-scrollbar-thumb {
  background: rgba(170, 185, 205, 0.18);
}

html[data-theme="dark"] .dialogs-list::-webkit-scrollbar-thumb:hover {
  background: rgba(170, 185, 205, 0.3);
}

/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 430px) {

  .sidebar-container {
    width: 100%;
    height: 100%;
  }

  .sidebar-header {
    padding: 19px 15px 15px;
  }

  .header-category-row {
    margin-top: 19px;
  }

  .category-title {
    font-size: 21px;
  }

  .search-section {
    padding: 14px 11px 10px;
  }

  .list-header {
    padding: 10px 15px 12px;
  }

  .dialogs-list {
    padding: 0 7px 20px;
  }

  .dialog-skeleton {
    min-height: 92px;
  }

  .category-dropdown {
    width: 180px;
  }

}

/* ============================================================
   REDUCED MOTION
============================================================ */

@media (prefers-reduced-motion: reduce) {

  .shimmer {
    animation: none;
  }

  .create-chat-button,
  .category-chevron,
  .category-option,
  .category-dropdown-enter-active,
  .category-dropdown-leave-active,
  .clear-search-button,
  .reset-search-button,
  .dialog-delete-enter-active,
  .dialog-delete-leave-active,
  .dialog-delete-move {
    transition: none;
  }

}

</style>
